// src/components/auth/Login.jsx
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import { useAuth } from "../../firebase/contexts/AuthContext";
import {
  ColorButton,
  RoundedCard,
  TeldioTextField,
} from "../../MUI_Components/Components";
import logo from "../../assets/Teldio-Logo-Color.png";

const authErrors = {
  "auth/invalid-email": "Email address not valid",
  "auth/wrong-password": "Incorrect username or password",
  "auth/user-not-found": "User not found",
  "auth/too-many-requests": "Too many login attempts, please try again later",
};

export default function Login() {
  const { login, resetPassword } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  async function handleSubmit(e) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const userEmail = data.get("email");
    const password = data.get("password");

    if (!userEmail || !password) {
      setError("You must enter an email and password");
      return;
    }

    try {
      setError("");
      setLoading(true);
      await login(userEmail, password);
      navigate("/");
    } catch (err) {
      console.log(err.code);
      if (authErrors[err.code]) {
        setError(authErrors[err.code]);
      } else {
        setError("Failed to log in");
      }
    }
    setLoading(false);
  }

  async function handleForgotPassword() {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    try {
      setError("");
      setLoading(true);
      await resetPassword(email);
      setResetSent(true);
    } catch (err) {
      if (err.code === "auth/user-not-found") {
        setError("No user found with this email address");
      } else {
        setError("Failed to send password reset email");
      }
    }
    setLoading(false);
  }

  return (
    <div
      style={{
        height: "100vh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f5f5f5",
      }}
    >
      <RoundedCard>
        <Container component="main" maxWidth="xs">
          <Box
            sx={{
              marginTop: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img src={logo} alt="Teldio logo" style={{ width: "150px" }} />
            <Typography component="h1" variant="h5" sx={{ marginTop: "30px" }}>
              Sign in
            </Typography>
            {error && (
              <Alert severity="error" sx={{ marginTop: "5px", width: "100%" }}>
                {error}
              </Alert>
            )}
            {resetSent && (
              <Alert
                severity="success"
                sx={{ marginTop: "5px", width: "100%" }}
              >
                Password reset email sent! Check your inbox.
              </Alert>
            )}
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ mt: 3, width: "100%" }}
            >
              <TeldioTextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TeldioTextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <ColorButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                >
                  Sign In
                </ColorButton>
              </div>
            </Box>
          </Box>
        </Container>
      </RoundedCard>
    </div>
  );
}
